import { setTokenProvider } from '@/lib/api';

/**
 * Wires the embed's bearer token from the URL fragment the `/canvas` SSO handler
 * appended (`#token=…`).
 *
 * The token rides the fragment, not the query string, so it never reaches a
 * server and is not written to logs or the `Referer`. Registering it as the API
 * bearer provider is what lets the embed authenticate **inside a cross-site
 * Salesforce iframe**, where the session cookie is a third-party cookie the
 * browser routinely refuses to send back. After reading it we strip it from the
 * URL so it does not linger in the iframe's history.
 *
 * Must run before the first API call (i.e. before the embed renders).
 */
export function installEmbedAuth(): void {
  try {
    const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
    if (!raw) return;
    const params = new URLSearchParams(raw);
    const token = params.get('token');
    if (!token) return;

    setTokenProvider(() => Promise.resolve(token));

    params.delete('token');
    const rest = params.toString();
    const cleaned = window.location.pathname + window.location.search + (rest ? `#${rest}` : '');
    window.history.replaceState(null, '', cleaned);
  } catch {
    // A blocked location/history API must not break the embed; it simply falls
    // back to the session cookie where the browser allows it.
  }
}
